import { globalEventBus } from '../core/EventBus.js';
import { AchievementEvents } from '../core/AchievementSystem.js';
import { i18n } from '../utils/I18n.js';

export class AchievementToast {
    constructor() {
        this.events = globalEventBus;
        this.container = null;
        this.queue = [];
        this.isShowing = false;

        this.init();
    }

    init() {
        // Create container
        this.container = document.createElement('div');
        this.container.id = 'achievement-toast-container';
        Object.assign(this.container.style, {
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: '9999',
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        });
        document.body.appendChild(this.container);

        // Bind events
        this.events.on(AchievementEvents.ACHIEVEMENT_UNLOCKED, (achievement) => {
            this.show(achievement);
        });

        // Add CSS styles
        this.addStyles();
    }

    addStyles() {
        if (document.getElementById('achievement-toast-styles')) return;

        const style = document.createElement('style');
        style.id = 'achievement-toast-styles';
        style.textContent = `
            @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap');

            .achievement-toast {
                font-family: 'Quicksand', sans-serif;
                background: linear-gradient(135deg, #FDF6EC 0%, #FAF0E4 100%);
                color: #5D4E42;
                padding: 18px 22px;
                border-radius: 20px;
                display: flex;
                align-items: center;
                gap: 16px;
                min-width: 320px;
                max-width: 400px;
                box-shadow:
                    0 8px 32px rgba(93, 78, 66, 0.2),
                    0 2px 8px rgba(93, 78, 66, 0.1);
                border: 1px solid rgba(201, 167, 125, 0.2);
                transform: translateX(120%);
                transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                pointer-events: auto;
                position: relative;
                overflow: hidden;
            }

            .achievement-toast::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 4px;
                background: linear-gradient(90deg, #E8A598, #F6D365, #9DC0A3);
            }

            .achievement-toast.show {
                transform: translateX(0);
            }

            .achievement-toast .toast-icon-wrapper {
                width: 52px;
                height: 52px;
                background: linear-gradient(135deg, #F6D365, #E8A598);
                border-radius: 14px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1.6rem;
                flex-shrink: 0;
                box-shadow: 0 4px 12px rgba(232, 165, 152, 0.3);
                animation: iconPulse 0.6s ease-out;
            }

            @keyframes iconPulse {
                0% { transform: scale(0.5); opacity: 0; }
                50% { transform: scale(1.15); }
                100% { transform: scale(1); opacity: 1; }
            }

            .achievement-toast .achievement-toast-label {
                font-size: 0.75rem;
                font-weight: 600;
                color: #9DC0A3;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin-bottom: 4px;
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .achievement-toast .achievement-toast-label svg {
                width: 14px;
                height: 14px;
            }

            .achievement-toast .achievement-toast-info {
                display: flex;
                flex-direction: column;
                flex: 1;
                min-width: 0;
            }

            .achievement-toast .achievement-toast-title {
                font-weight: 700;
                font-size: 1.1rem;
                color: #5D4E42;
                margin-bottom: 2px;
                line-height: 1.3;
            }

            .achievement-toast .achievement-toast-desc {
                font-size: 0.9rem;
                color: #8B7355;
                line-height: 1.4;
            }

            .achievement-toast .toast-sparkle {
                position: absolute;
                width: 6px;
                height: 6px;
                background: #F6D365;
                border-radius: 50%;
                animation: sparkle 1s ease-out forwards;
            }

            @keyframes sparkle {
                0% {
                    transform: scale(0) translate(0, 0);
                    opacity: 1;
                }
                100% {
                    transform: scale(1.5) translate(var(--tx), var(--ty));
                    opacity: 0;
                }
            }

            /* Mobile responsiveness */
            @media (max-width: 480px) {
                .achievement-toast {
                    min-width: auto;
                    max-width: calc(100vw - 48px);
                    padding: 14px 16px;
                }

                .achievement-toast .toast-icon-wrapper {
                    width: 44px;
                    height: 44px;
                    font-size: 1.4rem;
                }
            }
        `;
        document.head.appendChild(style);
    }

    show(achievement) {
        const toast = document.createElement('div');
        toast.className = 'achievement-toast';
        toast.innerHTML = `
            <div class="toast-icon-wrapper">${achievement.icon}</div>
            <div class="achievement-toast-info">
                <div class="achievement-toast-label">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z"/>
                    </svg>
                    ${i18n.t('achievement.toast_unlocked')}
                </div>
                <div class="achievement-toast-title">${achievement.title}</div>
                <div class="achievement-toast-desc">${achievement.description}</div>
            </div>
        `;

        // Add sparkle effects
        this.addSparkles(toast);

        this.container.appendChild(toast);

        // Force reflow
        toast.offsetHeight;

        // Show with animation
        requestAnimationFrame(() => {
            toast.classList.add('show');
        });

        // Hide and remove
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => {
                if (toast.parentNode) toast.parentNode.removeChild(toast);
            }, 500); // Wait for transition
        }, 4500); // Display duration
    }

    addSparkles(toast) {
        // Create several sparkle elements
        for (let i = 0; i < 6; i++) {
            const sparkle = document.createElement('div');
            sparkle.className = 'toast-sparkle';

            // Random position around the icon
            sparkle.style.left = `${30 + Math.random() * 40}px`;
            sparkle.style.top = `${20 + Math.random() * 40}px`;

            // Random direction for animation
            sparkle.style.setProperty('--tx', `${(Math.random() - 0.5) * 60}px`);
            sparkle.style.setProperty('--ty', `${(Math.random() - 0.5) * 60}px`);
            sparkle.style.animationDelay = `${Math.random() * 0.3}s`;

            toast.appendChild(sparkle);
        }
    }
}
